import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, EventEmitter, Component, ChangeDetectionStrategy, HostBinding, Input, HostListener, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import * as i1$1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i6 from '@angular/material/divider';
import { MatDividerModule } from '@angular/material/divider';
import * as i2 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i5 from '@angular/material/menu';
import { MatMenuModule } from '@angular/material/menu';
import { merge } from 'lodash-es';
import { filter, map, takeUntil, distinctUntilChanged, tap } from 'rxjs/operators';
import { ReplaySubject, BehaviorSubject } from 'rxjs';
import * as i1$2 from '@angular/platform-browser';

class NavComponent {
    constructor() {
        this.value = null;
        this.permanent = false;
    }
    updateValue(value) {
        this.value = value;
    }
    clear() {
        this.value = null;
    }
}

class FsNavComponents {
    constructor(_navUpdates) {
        this._navUpdates = _navUpdates;
        this._components = new Map();
    }
    /**
     * Set special value for component by name
     * @param name { string }
     * @param value
     * @param permanent - do not clear component value when url changed
     */
    setComponentValue(name, value, permanent = null) {
        this.createComponentIfNotExists(name);
        if (this._components.has(name)) {
            const component = this._components.get(name);
            component.updateValue(value);
            if (permanent !== null) {
                component.permanent = permanent;
            }
        }
        this._navUpdates.updateComponent(name, this._components.get(name));
    }
    /**
     * Show component
     * @param name { string }
     */
    showComponent(name) {
        this._navUpdates.showComponent(name);
    }
    /**
     * Hide component
     * @param name { string }
     */
    hideComponent(name) {
        this._navUpdates.hideComponent(name);
    }
    /**
     * Clear all components
     */
    clear() {
        this._components.forEach((component, name) => {
            if (!component.permanent) {
                component.clear();
                this._navUpdates.clearComponent(name);
            }
        });
    }
    /**
     * @param name { string }
     */
    createComponentIfNotExists(name) {
        if (!this._components.has(name)) {
            const component = new NavComponent();
            this._components.set(name, component);
        }
    }
}

var NavActionType;
(function (NavActionType) {
    NavActionType["basic"] = "basic";
    NavActionType["raised"] = "raised";
    NavActionType["icon"] = "icon";
    NavActionType["fab"] = "fab";
    NavActionType["miniFab"] = "mini-fab";
})(NavActionType || (NavActionType = {}));
class NavAction {
    constructor(config = {}) {
        this.classArray = [];
        this._init(config);
    }
    _init(value = {}) {
        var _a, _b;
        this.icon = value.icon;
        this.label = value.label;
        this.click = (_a = value.click) !== null && _a !== void 0 ? _a : ((event) => { });
        this.className = value.className;
        this.type = (_b = value.type) !== null && _b !== void 0 ? _b : NavActionType.basic;
        this.image = value.image;
        this.url = value.url;
        if (this.className) {
            this.classArray = this.className
                .split(' ')
                .reduce((acc, elem) => {
                acc.push(elem);
                return acc;
            }, []);
        }
        if (this.image) {
            this.classArray.push('custom-icon-action');
        }
    }
}

class FsNavActions {
    constructor(_navUpdates) {
        this._navUpdates = _navUpdates;
        this._actions = new Map();
    }
    /**
     * Set action (button) for special component
     * @param name { string }
     * @param action { UrlInfoAction }
     */
    addAction(name, action) {
        if (!this._actions.has(name)) {
            this._actions.set(name, []);
        }
        const element = this._actions.get(name);
        const elementAction = new NavAction(action);
        element.push(elementAction);
        this._navUpdates.updateAction(name, element);
    }
    setAction(name, action) {
        this._actions.set(name, []);
        this.addAction(name, action);
    }
    /**
     * Add actions (buttons) for special component
     * @param name { string }
     * @param actions : { UrlInfoAction[] }
     */
    addActions(name, actions) {
        if (actions) {
            actions.forEach((action) => {
                this.addAction(name, action);
            });
        }
    }
    setActions(name, actions) {
        this._actions.set(name, []);
        this.addActions(name, actions);
    }
    /**
     * Show Action
     * @param name { string }
     */
    showAction(name) {
        this._navUpdates.showComponent(name);
    }
    /**
     * Hide Action
     * @param name { string }
     */
    hideAction(name) {
        this._navUpdates.hideComponent(name);
    }
    /**
     * Clear all actions
     */
    clear() {
        this._actions.forEach((actions) => {
            actions.length = 0;
        });
        this._actions.clear();
    }
}

class NavDropDownMenu {
    constructor(icon = '') {
        this.icon = icon;
        this.groups = new Map();
    }
    addAction(action, group) {
        if (!this.groups.has(group)) {
            this.groups.set(group, []);
        }
        const actions = this.groups.get(group);
        actions.push(new NavAction(action));
    }
    clear() {
        this.groups.forEach((actions) => {
            actions.length = 0;
        });
    }
}

class FsNavMenus {
    constructor(_navUpdates) {
        this._navUpdates = _navUpdates;
        this._menus = new Map();
    }
    /**
     * Set menu action (item) for specified menu
     * @param name { string }
     * @param action { UrlInfoAction }
     * @param group { string }
     * @param icon { string }
     */
    setMenuAction(name, action, group = 'default', icon = '') {
        if (!this._menus.has(name)) {
            this._menus.set(name, new NavDropDownMenu(icon));
        }
        const menu = this._menus.get(name);
        menu.addAction(action, group);
        this._navUpdates.updateMenu(name, menu);
    }
    /**
     * Set menu actions (items) for specified menu
     * @param name { string }
     * @param actions : { UrlInfoAction[] }
     * @param group { string }
     * @param icon { string }
     */
    setMenuActions(name, actions, group = 'default', icon = '') {
        if (actions) {
            actions.forEach((action) => {
                this.setMenuAction(name, action, group, icon);
            });
        }
    }
    /**
     * Set icon for menu
     * @param name
     * @param icon
     */
    setMenuIcon(name, icon) {
        if (!this._menus.has(name)) {
            this._menus.set(name, new NavDropDownMenu(icon));
        }
        const menu = this._menus.get(name);
        menu.icon = icon;
    }
    /**
     * Show Menu
     * @param name
     */
    showMenu(name) {
        this._navUpdates.showMenu(name);
    }
    /**
     * Hide Menu
     * @param name
     */
    hideMenu(name) {
        this._navUpdates.hideMenu(name);
    }
    /**
     * Clear all menus
     */
    clear() {
        this._menus.forEach((menu) => {
            menu.clear();
        });
        this._menus.clear();
        this._navUpdates.clearMenu('__all__', null);
    }
}

var FsNavUpdateType;
(function (FsNavUpdateType) {
    FsNavUpdateType["data"] = "data";
    FsNavUpdateType["update"] = "update";
    FsNavUpdateType["clear"] = "clear";
    FsNavUpdateType["show"] = "show";
    FsNavUpdateType["hide"] = "hide";
})(FsNavUpdateType || (FsNavUpdateType = {}));
var FsNavUpdateTarget;
(function (FsNavUpdateTarget) {
    FsNavUpdateTarget["all"] = "__all__";
    FsNavUpdateTarget["component"] = "component";
    FsNavUpdateTarget["menu"] = "menu";
    FsNavUpdateTarget["actions"] = "actions";
})(FsNavUpdateTarget || (FsNavUpdateTarget = {}));
class FsNavUpdatesService {
    constructor() {
        this._updated = new ReplaySubject(30);
    }
    updateRouteData(data) {
        this.update(FsNavUpdateTarget.all, FsNavUpdateType.data, '__all__', data);
    }
    updateComponent(name, value) {
        this.update(FsNavUpdateTarget.component, FsNavUpdateType.update, name, value);
    }
    showComponent(name) {
        this.update(FsNavUpdateTarget.component, FsNavUpdateType.show, name);
    }
    hideComponent(name) {
        this.update(FsNavUpdateTarget.component, FsNavUpdateType.hide, name);
    }
    clearComponent(name) {
        this.update(FsNavUpdateTarget.component, FsNavUpdateType.clear, name, null);
    }
    updateAction(name, value) {
        this.update(FsNavUpdateTarget.actions, FsNavUpdateType.update, name, value);
    }
    updateMenu(name, value) {
        this.update(FsNavUpdateTarget.menu, FsNavUpdateType.update, name, value);
    }
    showMenu(name) {
        this.update(FsNavUpdateTarget.menu, FsNavUpdateType.show, name);
    }
    hideMenu(name) {
        this.update(FsNavUpdateTarget.menu, FsNavUpdateType.hide, name);
    }
    clearMenu(name, value) {
        this.update(FsNavUpdateTarget.menu, FsNavUpdateType.clear, name, value);
    }
    componentUpdated$(name, destroy = null) {
        return this.onUpdate(FsNavUpdateTarget.component, name, destroy);
    }
    actionUpdated$(name, destroy = null) {
        return this.onUpdate(FsNavUpdateTarget.actions, name, destroy);
    }
    menuUpdated$(name, destroy = null) {
        return this.onUpdate(FsNavUpdateTarget.menu, name, destroy);
    }
    update(target, type, name, value = null) {
        this._updated.next({
            target,
            payload: {
                name,
                type,
                value: value
            }
        });
    }
    onUpdate(target, name, destroy) {
        const updater = this._updated.pipe(filter((event) => {
            return (event.target === target || event.target == FsNavUpdateTarget.all)
                && (event.payload.name === name || event.payload.name === '__all__');
        }), map((event) => {
            return event.payload;
        }));
        return destroy ? updater.pipe(takeUntil(destroy)) : updater;
    }
}
FsNavUpdatesService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavUpdatesService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsNavUpdatesService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavUpdatesService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavUpdatesService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

const FS_NAV_DEFAULT_CONFIG = new InjectionToken('fs-nav.default-config');
const FS_NAV_CONFIG = new InjectionToken('fs-nav.config');

class FsNavStackService {
    // private _handlers: {[key: string]: DetachedRouteHandle} = {}; // Do not remove!
    constructor(_activatedRoute, _router, _navUpdates, _defaultConfig) {
        this._activatedRoute = _activatedRoute;
        this._router = _router;
        this._navUpdates = _navUpdates;
        this._defaultConfig = _defaultConfig;
        this.components = new FsNavComponents(this._navUpdates);
        this.actions = new FsNavActions(this._navUpdates);
        this.menus = new FsNavMenus(this._navUpdates);
        this._urlsStack = [];
        this._stopBackToUrls = [];
        this._activeRoute = new BehaviorSubject(null);
        this._lastOperationIsBack = false;
        this._browserBack = false;
        this.subscribeToRouteChange();
        if (this._defaultConfig.watchBrowserBackButton) {
            this.subscribeToBrowserBack();
        }
    }
    get activeRoute() {
        return this._activeRoute.getValue();
    }
    get lastOperationIsBack() {
        return this._lastOperationIsBack;
    }
    get urlsStack() {
        return this._urlsStack.slice();
    }
    get stackRouteChangeSubscription() {
        return this.routeChangeSubscription();
    }
    /**
     * Important method that do subscribe to route changes and setup route
     */
    subscribeToRouteChange() {
        this.routeChangeSubscription()
            .subscribe(this.setupActivatedRoute.bind(this));
    }
    /**
     * Prevent default behaviour of browser back button
     */
    subscribeToBrowserBack() {
        window.addEventListener('popstate', () => {
            if (!this._lastOperationIsBack) {
                this._browserBack = true;
                // Hack to prevent native back button
                history.go(1);
                this.goBack();
            }
        });
    }
    /**
     * Set activated route as stop route. You will be unable to go back to this url
     */
    setActiveUrlAsStop() {
        if (this._stopBackToUrls.indexOf(this.activeRoute.path) === -1) {
            this._stopBackToUrls.push(this.activeRoute.path);
        }
    }
    /**
     * Add url to current urls stack
     * @param item
     */
    addUrlToStack(item) {
        const lastStackItem = this._urlsStack[this._urlsStack.length - 1];
        if (!lastStackItem || (lastStackItem && lastStackItem.path !== item.path)) {
            this._urlsStack.push(item);
        }
        this._lastOperationIsBack = false;
    }
    /**
     * Remove all information about stacked pages and handlers
     */
    resetStack() {
        // for (const key in this._handlers) {
        //   if (this._handlers.hasOwnProperty(key)) {
        //     // Destroy component
        //     this.deactivateOutlet(this._handlers[key])
        //   }
        // }
        this._urlsStack.length = 0;
    }
    /**
     * Set active route path based on passed route path
     * @param route
     * @param data
     */
    setActiveRoute(route, data) {
        const save = !data.lastChild;
        const path = save ? this.getFullRoutePath(route) : this.getRoutePath(route.parent);
        if (!save && this.activeRoute && this.activeRoute.path === path) {
            this.activeRoute.backCounts++;
            this.activeRoute.fullPath = this.getFullRoutePath(route);
        }
        else {
            this._activeRoute.next({
                path: path,
                fullPath: this.getFullRoutePath(route),
                data: data,
                save: save,
                backCounts: 1
            });
        }
        this._lastOperationIsBack = false;
        this._browserBack = false;
    }
    /**
     * Set last stack route as active (restore)
     */
    setLastStackRouteAsActiveRoute() {
        const prevItem = this._urlsStack[this._urlsStack.length - 1];
        this._activeRoute.next(prevItem);
        this._lastOperationIsBack = false;
        this._browserBack = false;
    }
    /**
     * @param route
     */
    getRoutePath(route) {
        return this.getHeadPath(route);
    }
    /**
     * Recursevly get full path for route
     * @param route
     */
    getFullRoutePath(route) {
        if (route.firstChild) {
            return this.getTailPath(route);
        }
        else {
            return this.getHeadPath(route);
        }
    }
    /**
     * Window history go back to N steps
     * @param steps
     */
    goBack(steps = null) {
        if (!this._defaultConfig.watchBrowserBackButton) {
            window.history.back();
            return;
        }
        if (steps) {
            window.history.go(-steps);
            return;
        }
        const stackSize = this._urlsStack.length;
        // Can't go back because stack is empty
        if (stackSize === 0) {
            this._router.navigateByUrl('/');
            return;
        }
        const lastStackItem = this._urlsStack[stackSize - 1];
        const routeDelta = this.activeRoute.backCounts;
        const { delta: backDelta, depth: backCount } = this.backDelta(lastStackItem, 0);
        let delta = routeDelta + backDelta;
        if (backDelta > 0 && lastStackItem.save && lastStackItem.data.history) {
            delta -= 1;
        }
        // !!! @Ref { stack_pop } - if target url for go back has save false then
        // will do stack.pop() in setupActivated route
        const reduceStackBy = !this._urlsStack[stackSize - backCount].save
            ? backCount - 1
            : backCount;
        this._urlsStack.splice(-reduceStackBy, reduceStackBy);
        // Increase delta because of history manipulation was made.
        delta *= -2;
        if (delta !== 0) {
            this._lastOperationIsBack = true;
            window.history.go(delta);
        }
    }
    /**
     * Method that counts number of steps to go back based on banned urls
     * @param prevStackItem
     * @param delta
     * @param depth
     */
    backDelta(prevStackItem, delta, depth = 1) {
        if (prevStackItem && this._stopBackToUrls.length > 0) {
            // In case when we do back between history: false pages - we need to go just back
            if (this._stopBackToUrls.indexOf(this.activeRoute.path) === -1 &&
                this._stopBackToUrls.indexOf(prevStackItem.path) > -1) {
                delta += prevStackItem.backCounts;
                depth++;
                return this.backDelta(this._urlsStack[this._urlsStack.length - depth], delta, depth);
            }
        }
        return {
            delta,
            depth
        };
    }
    /**
     * Support method for getFullRoutePath
     * @param route
     * @param path
     */
    getHeadPath(route, path = '') {
        if (route.parent !== null) {
            return this.getHeadPath(route.parent, path) + '/' + route.url.join('/');
        }
        else {
            return route.url.join('/');
        }
    }
    /**
     * Support method for getFullRoutePath
     * @param route
     * @param path
     */
    getTailPath(route, path = '') {
        if (route.firstChild) {
            return `/${route.url.join('/')}/${this.getTailPath(route.firstChild, path)}`;
        }
        else {
            return `${route.url.join('/')}`;
        }
    }
    /**
     * Lookup for element which can be saved (lastChild: false)
     */
    getLastSaveElement() {
        return this._urlsStack.slice().reverse().find((item) => item.save);
    }
    // /**
    //  * Destroy component !!! DO NOT REMOVE !!!
    //  * @param {DetachedRouteHandle} handle
    //  */
    // private deactivateOutlet(handle: DetachedRouteHandle): void {
    //   if (handle === null) { return; }
    //   const componentRef: ComponentRef<any> = handle['componentRef'];
    //   if (componentRef) {
    //     componentRef.destroy()
    //   }
    // }
    /**
     * Important method - do operations for setup activated route
     * @param route
     */
    setupActivatedRoute(route) {
        this.components.clear();
        this.actions.clear();
        this.menus.clear();
        // We don't need to do any manipulations with stack if back button logic disabled
        if (this._defaultConfig.watchBrowserBackButton) {
            const routePath = !this._routeData.lastChild
                ? this.getFullRoutePath(route.snapshot)
                : this.getRoutePath(route.snapshot.parent);
            if (!this.lastOperationIsBack && this.activeRoute && this.activeRoute.path !== routePath) {
                this.addUrlToStack(this.activeRoute);
            }
            if (!this.lastOperationIsBack) {
                // Hack to prevent native back button
                history.pushState(null, null, location.href);
            }
            /**
             * Make a choice:
             * 1) Restore route from stack if lastChild: true
             * 2) Set from activated route
             */
            if (this.lastOperationIsBack && this._routeData.lastChild) {
                this.setLastStackRouteAsActiveRoute();
                // !!! @Ref { stack_pop } - goBack logic
                this._urlsStack.pop();
            }
            else {
                this.setActiveRoute(route.snapshot, this._routeData);
            }
            this._navUpdates.updateRouteData(this._routeData);
            if (this._routeData.root === true) {
                this.resetStack();
            }
            if (this._routeData.history === false) {
                this.setActiveUrlAsStop();
            }
        }
        else {
            this._navUpdates.updateRouteData(this._routeData);
        }
    }
    /**
     * Get route data from Activated route snapshot
     * @param route
     * @param key
     */
    getRouteData(route, key = null) {
        if (key) {
            return route && route.data && route.data[key] || null;
        }
        else {
            return route && route.data || null;
        }
    }
    /**
     * Subscription that used for listening routes change
     */
    routeChangeSubscription() {
        return this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd), distinctUntilChanged((prev, curr) => {
            var _a, _b;
            const prevUrl = (_a = prev.url) === null || _a === void 0 ? void 0 : _a.split('?')[0];
            const currUrl = (_b = curr.url) === null || _b === void 0 ? void 0 : _b.split('?')[0];
            return prevUrl === currUrl;
        }), map(() => {
            return this._activatedRoute;
        }), tap(() => {
            this._routeData = { root: false, history: true, lastChild: false };
        }), map((route) => {
            while (route.firstChild) {
                route = route.firstChild;
                this._routeData = Object.assign({}, this._routeData, this.getRouteData(route.snapshot, 'fsNav'));
            }
            return route;
        }), tap(() => {
            if (this._routeData.rootChildren !== void 0) {
                this._routeData.root = this._routeData.rootChildren;
            }
        }), filter((route) => route.outlet === 'primary'));
    }
}
FsNavStackService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavStackService, deps: [{ token: i1.ActivatedRoute }, { token: i1.Router }, { token: FsNavUpdatesService }, { token: FS_NAV_DEFAULT_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FsNavStackService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavStackService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavStackService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.ActivatedRoute }, { type: i1.Router }, { type: FsNavUpdatesService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_NAV_DEFAULT_CONFIG]
                }] }]; } });

class FsNavBaseComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        this.navUpdates = navUpdates;
        this.navStack = navStack;
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.cdRef = cdRef;
        this.hidden = false;
        this.empty = true;
        this.root = false;
        this._destroy = new EventEmitter();
    }
    get name() {
        return this._name;
    }
    ngOnInit() {
        this.setSelfClass();
        this.subscriptions();
    }
    ngOnDestroy() {
        this._destroy.emit();
        this._destroy.complete();
    }
    setSelfClass() {
        this.renderer.addClass(this.elementRef.nativeElement, 'fs-nav-component-' + this._name);
    }
    subscriptions() {
        this.navUpdates.componentUpdated$(this._name, this._destroy)
            .pipe(takeUntil(this._destroy))
            .subscribe((payload) => this.payloadUpdated(payload));
    }
    payloadUpdated(payload) {
        switch (payload.type) {
            case FsNavUpdateType.update:
                {
                    this.updated(payload);
                }
                break;
            case FsNavUpdateType.show:
                {
                    this.updatedShow();
                }
                break;
            case FsNavUpdateType.hide:
                {
                    this.updatedHide();
                }
                break;
            case FsNavUpdateType.clear:
                {
                    this.updatedClear();
                }
                break;
            case FsNavUpdateType.data:
                {
                    this.updatedData(payload);
                }
                break;
            default: {
                this.updatedDefault(payload);
            }
        }
        this.cdRef.markForCheck();
    }
    updatedShow() {
        this.hidden = false;
    }
    updatedHide() {
        this.hidden = true;
    }
    updatedClear() {
        this.empty = true;
        this.value = null;
    }
    updated(payload) {
        this.value = payload.value;
        if (this.value instanceof Array) {
            this.empty = !this.value.length;
        }
        else {
            this.empty = !payload.value;
        }
    }
    updatedData(payload) {
        this.root = payload.value && payload.value.root;
    }
    updatedDefault(payload) { }
}
FsNavBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavBaseComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavBaseComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavBaseComponent, selector: "ng-component", host: { properties: { "class.hidden": "this.hidden", "class.empty": "this.empty", "class.root": "this.root" } }, ngImport: i0, template: '', isInline: true, styles: [":host-context(.hidden),:host-context(.empty){display:none}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavBaseComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    styleUrls: ['nav-base.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { hidden: [{
                type: HostBinding,
                args: ['class.hidden']
            }], empty: [{
                type: HostBinding,
                args: ['class.empty']
            }], root: [{
                type: HostBinding,
                args: ['class.root']
            }] } });

class FsNavActionsComponent extends FsNavBaseComponent {
    constructor() {
        super(...arguments);
        this.selfClass = true;
        this._type = FsNavUpdateTarget.actions;
    }
    set componentName(value) {
        this._name = value;
    }
    ;
    subscriptions() {
        this.navUpdates.actionUpdated$(this._name, this._destroy)
            .pipe(takeUntil(this._destroy))
            .subscribe((payload) => this.payloadUpdated(payload));
    }
}
FsNavActionsComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavActionsComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
FsNavActionsComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavActionsComponent, selector: "[fsNavActions]", inputs: { componentName: ["fsNavActions", "componentName"] }, host: { properties: { "class.fs-nav-actions": "this.selfClass" } }, usesInheritance: true, ngImport: i0, template: "<ng-container *ngFor=\"let action of value\"\n              [ngSwitch]=\"action.type\">\n  <!-- Basic button -->\n  <ng-container *ngSwitchCase=\"'basic'\">\n    <!-- Button case -->\n    <button\n      mat-button\n      *ngIf=\"!action.url\"\n      (click)=\"action.click($event)\"\n      [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </button>\n\n    <!-- URL case -->\n    <a mat-button\n       *ngIf=\"action.url\"\n       [routerLink]=\"[action.url]\"\n       (click)=\"action.click($event)\"\n       [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </a>\n  </ng-container>\n\n  <!-- Raised button -->\n  <ng-container *ngSwitchCase=\"'raised'\">\n    <!-- Button case -->\n    <button\n      mat-raised-button\n      *ngIf=\"!action.url\"\n      (click)=\"action.click($event)\"\n      [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </button>\n\n    <!-- URL case -->\n    <a mat-raised-button\n       *ngIf=\"action.url\"\n       [routerLink]=\"[action.url]\"\n       (click)=\"action.click($event)\"\n       [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </a>\n  </ng-container>\n\n  <!-- Icon button -->\n  <ng-container *ngSwitchCase=\"'icon'\">\n    <!-- Button case -->\n    <button\n      mat-icon-button\n      *ngIf=\"!action.url\"\n      (click)=\"action.click($event)\"\n      [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </button>\n\n    <!-- URL case -->\n    <a mat-icon-button\n       *ngIf=\"action.url\"\n       [routerLink]=\"[action.url]\"\n       (click)=\"action.click($event)\"\n       [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </a>\n  </ng-container>\n\n  <!-- Fab button -->\n  <ng-container *ngSwitchCase=\"'fab'\">\n    <!-- Button case -->\n    <button\n      mat-fab\n      *ngIf=\"!action.url\"\n      (click)=\"action.click($event)\"\n      [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </button>\n\n    <!-- URL case -->\n    <a mat-fab\n       *ngIf=\"action.url\"\n       [routerLink]=\"[action.url]\"\n       (click)=\"action.click($event)\"\n       [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </a>\n  </ng-container>\n\n  <!-- Mini Fab button -->\n  <ng-container *ngSwitchCase=\"'mini-fab'\">\n    <!-- Button case -->\n    <button\n      mat-mini-fab\n      *ngIf=\"!action.url\"\n      (click)=\"action.click($event)\"\n      [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </button>\n\n    <!-- URL case -->\n    <a mat-mini-fab\n       *ngIf=\"action.url\"\n       [routerLink]=\"[action.url]\"\n       (click)=\"action.click($event)\"\n       [ngClass]=\"action.classArray\"\n    >\n      <ng-template [ngTemplateOutlet]=\"buttonContent\"></ng-template>\n    </a>\n  </ng-container>\n\n  <ng-template #buttonContent let-value=\"value\">\n    <mat-icon *ngIf=\"action.icon && !action.image\">{{action.icon}}</mat-icon>\n    <img *ngIf=\"action.image\" [src]=\"action.image\">\n\n    {{action.label}}\n  </ng-template>\n</ng-container>\n", styles: ["::ng-deep .align-icon{padding:0 16px}::ng-deep .align-icon .mat-icon{vertical-align:middle}::ng-deep .custom-icon-action{padding:0 16px}::ng-deep .custom-icon-action img{vertical-align:middle}\n"], components: [{ type: i1$1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i1$1.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i3.NgSwitch, selector: "[ngSwitch]", inputs: ["ngSwitch"] }, { type: i3.NgSwitchCase, selector: "[ngSwitchCase]", inputs: ["ngSwitchCase"] }, { type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i3.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i1.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["routerLink", "target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavActions]',
                    templateUrl: 'nav-actions.component.html',
                    styleUrls: ['nav-actions.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { componentName: [{
                type: Input,
                args: ['fsNavActions']
            }], selfClass: [{
                type: HostBinding,
                args: ['class.fs-nav-actions']
            }] } });

class FsNavBackComponent extends FsNavBaseComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        super(navUpdates, navStack, elementRef, renderer, cdRef);
        this.selfClass = true;
        this._type = FsNavUpdateTarget.component;
        this._name = 'back';
    }
    goBack() {
        this.navStack.goBack();
    }
}
FsNavBackComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavBackComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavBackComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavBackComponent, selector: "[fsNavBack]", host: { listeners: { "click": "goBack()" }, properties: { "class.fs-nav-back": "this.selfClass" } }, usesInheritance: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [""], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavBackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavBack]',
                    template: '<ng-content></ng-content>',
                    styles: [''],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { selfClass: [{
                type: HostBinding,
                args: ['class.fs-nav-back']
            }], goBack: [{
                type: HostListener,
                args: ['click']
            }] } });

class FsNavComponentComponent extends FsNavBaseComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        super(navUpdates, navStack, elementRef, renderer, cdRef);
        this._type = FsNavUpdateTarget.component;
    }
    set name(value) {
        this._name = value;
    }
    ;
}
FsNavComponentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavComponentComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavComponentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavComponentComponent, selector: "[fsNavComponent]", inputs: { contentType: ["type", "contentType"], name: ["fsNavComponent", "name"] }, usesInheritance: true, ngImport: i0, template: "<img [src]=\"value?.value\" *ngIf=\"contentType=='image' && value?.value; else elseText\">\n<ng-template #elseText>{{ value?.value }}</ng-template>\n", styles: [":host-context(.hidden),:host-context(.empty){display:none}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavComponentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavComponent]',
                    templateUrl: './nav-component.component.html',
                    styleUrls: ['../nav-base/nav-base.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { contentType: [{
                type: Input,
                args: ['type']
            }], name: [{
                type: Input,
                args: ['fsNavComponent']
            }] } });

;
class FsNavMenuComponent extends FsNavBaseComponent {
    constructor(navUpdates, navStack, _elementRef, _renderer, cdRef) {
        super(navUpdates, navStack, _elementRef, _renderer, cdRef);
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this.selfClass = true;
        this.menu = null;
        this.groups = [];
        this.actions = new Map();
        this._type = FsNavUpdateTarget.menu;
    }
    set menuName(value) {
        this._name = value;
    }
    ;
    setSelfClass() {
        this.renderer.addClass(this.elementRef.nativeElement, 'fs-nav-menu-' + this._name);
    }
    subscriptions() {
        this.navUpdates.menuUpdated$(this._name, this._destroy)
            .subscribe((payload) => this.payloadUpdated(payload));
    }
    updated(payload) {
        this.menu = payload.value;
        if (this.menu) {
            this.groups = Array.from(this.menu.groups.keys());
            this.actions = this.menu.groups;
        }
    }
    updatedClear() {
        this.menu = null;
        this.groups.length = 0;
        this.actions.clear();
    }
}
FsNavMenuComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavMenuComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavMenuComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavMenuComponent, selector: "[fsNavMenu]", inputs: { menuName: ["fsNavMenu", "menuName"] }, host: { properties: { "class.fs-nav-menu": "this.selfClass" } }, usesInheritance: true, ngImport: i0, template: "<button mat-icon-button [matMenuTriggerFor]=\"menuActionsRef\" *ngIf=\"menu\">\n  <mat-icon>{{ menu?.icon || 'more_vert' }}</mat-icon>\n</button>\n<mat-menu #menuActionsRef>\n  <ng-container *ngFor=\"let group of groups; let groupIndex = index\">\n    <!-- Group name -->\n    <ng-template [ngIf]=\"group !== 'default'\">\n      <button mat-menu-item class=\"group-header\" [disabled]=\"true\">{{group}}</button>\n    </ng-template>\n\n    <!-- actions -->\n    <ng-container *ngFor=\"let action of actions.get(group)\">\n      <button mat-menu-item *ngIf=\"!action.url\" (click)=\"action.click($event)\">\n        <mat-icon *ngIf=\"action.icon && !action.image\">{{action.icon}}</mat-icon>\n        <img *ngIf=\"action.image\" [src]=\"action.image\">\n        {{action.label}}\n      </button>\n\n      <a mat-menu-item\n         *ngIf=\"action.url\"\n         [routerLink]=\"[action.url]\"\n         (click)=\"action.click($event)\"\n         [ngClass]=\"action.classArray\"\n      >\n        <mat-icon *ngIf=\"action.icon && !action.image\">{{action.icon}}</mat-icon>\n        <img *ngIf=\"action.image\" [src]=\"action.image\">\n        {{action.label}}\n      </a>\n    </ng-container>\n\n    <!-- divider -->\n    <ng-template [ngIf]=\"actions.get(group).length > 0 && groups.length - 1 > groupIndex\">\n      <mat-divider></mat-divider>\n    </ng-template>\n  </ng-container>\n</mat-menu>\n", styles: ["::ng-deep .align-icon{padding:0 16px}::ng-deep .align-icon .mat-icon{vertical-align:middle}::ng-deep .custom-icon-action{padding:0 16px}::ng-deep .custom-icon-action img{vertical-align:middle}\n"], components: [{ type: i1$1.MatButton, selector: "button[mat-button], button[mat-raised-button], button[mat-icon-button],             button[mat-fab], button[mat-mini-fab], button[mat-stroked-button],             button[mat-flat-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { type: i2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i5.MatMenu, selector: "mat-menu", exportAs: ["matMenu"] }, { type: i5.MatMenuItem, selector: "[mat-menu-item]", inputs: ["disabled", "disableRipple", "role"], exportAs: ["matMenuItem"] }, { type: i6.MatDivider, selector: "mat-divider", inputs: ["vertical", "inset"] }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.MatMenuTrigger, selector: "[mat-menu-trigger-for], [matMenuTriggerFor]", exportAs: ["matMenuTrigger"] }, { type: i3.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i1.RouterLinkWithHref, selector: "a[routerLink],area[routerLink]", inputs: ["routerLink", "target", "queryParams", "fragment", "queryParamsHandling", "preserveFragment", "skipLocationChange", "replaceUrl", "state", "relativeTo"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavMenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavMenu]',
                    templateUrl: 'nav-menu.component.html',
                    styleUrls: ['nav-menu.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { menuName: [{
                type: Input,
                args: ['fsNavMenu']
            }], selfClass: [{
                type: HostBinding,
                args: ['class.fs-nav-menu']
            }] } });

class FsNavSubtitleComponent extends FsNavComponentComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        super(navUpdates, navStack, elementRef, renderer, cdRef);
        this.subtitleClass = true;
        this._type = FsNavUpdateTarget.component;
        this._name = 'subtitle';
    }
    set name(value) {
        this._name = 'subtitle';
    }
    ;
}
FsNavSubtitleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavSubtitleComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavSubtitleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavSubtitleComponent, selector: "[fsNavSubtitle]", inputs: { name: ["fsNavComponent", "name"] }, host: { properties: { "class.fs-nav-subtitle": "this.subtitleClass" } }, usesInheritance: true, ngImport: i0, template: "<img [src]=\"value?.value\" *ngIf=\"contentType=='image' && value?.value; else elseText\">\n<ng-template #elseText>{{ value?.value }}</ng-template>\n", styles: [":host-context(.hidden),:host-context(.empty){display:none}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavSubtitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavSubtitle]',
                    templateUrl: '../nav-component/nav-component.component.html',
                    styleUrls: ['../nav-base/nav-base.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input,
                args: ['fsNavComponent']
            }], subtitleClass: [{
                type: HostBinding,
                args: ['class.fs-nav-subtitle']
            }] } });

class FsNavSupertitleComponent extends FsNavComponentComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        super(navUpdates, navStack, elementRef, renderer, cdRef);
        this.subtitleClass = true;
        this._type = FsNavUpdateTarget.component;
        this._name = 'supertitle';
    }
    set name(value) {
        this._name = 'supertitle';
    }
    ;
}
FsNavSupertitleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavSupertitleComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavSupertitleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavSupertitleComponent, selector: "[fsNavSupertitle]", inputs: { name: ["fsNavComponent", "name"] }, host: { properties: { "class.fs-nav-supertitle": "this.subtitleClass" } }, usesInheritance: true, ngImport: i0, template: "<img [src]=\"value?.value\" *ngIf=\"contentType=='image' && value?.value; else elseText\">\n<ng-template #elseText>{{ value?.value }}</ng-template>\n", styles: [":host-context(.hidden),:host-context(.empty){display:none}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavSupertitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavSupertitle]',
                    templateUrl: '../nav-component/nav-component.component.html',
                    styleUrls: ['../nav-base/nav-base.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input,
                args: ['fsNavComponent']
            }], subtitleClass: [{
                type: HostBinding,
                args: ['class.fs-nav-supertitle']
            }] } });

class FsNavTitleComponent extends FsNavComponentComponent {
    constructor(navUpdates, navStack, elementRef, renderer, cdRef) {
        super(navUpdates, navStack, elementRef, renderer, cdRef);
        this.subtitleClass = true;
        this._type = FsNavUpdateTarget.component;
        this._name = 'title';
    }
    set name(value) {
        this._name = 'title';
    }
    ;
}
FsNavTitleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavTitleComponent, deps: [{ token: FsNavUpdatesService }, { token: FsNavStackService }, { token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsNavTitleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsNavTitleComponent, selector: "[fsNavTitle]", inputs: { name: ["fsNavComponent", "name"] }, host: { properties: { "class.fs-nav-title": "this.subtitleClass" } }, usesInheritance: true, ngImport: i0, template: "<img [src]=\"value?.value\" *ngIf=\"contentType=='image' && value?.value; else elseText\">\n<ng-template #elseText>{{ value?.value }}</ng-template>\n", styles: [":host-context(.hidden),:host-context(.empty){display:none}\n"], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavTitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[fsNavTitle]',
                    templateUrl: '../nav-component/nav-component.component.html',
                    styleUrls: ['../nav-base/nav-base.component.scss'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: FsNavUpdatesService }, { type: FsNavStackService }, { type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { name: [{
                type: Input,
                args: ['fsNavComponent']
            }], subtitleClass: [{
                type: HostBinding,
                args: ['class.fs-nav-title']
            }] } });

class FsNavService {
    constructor(_stack) {
        this._stack = _stack;
        this._title$ = new BehaviorSubject({
            title: { value: '', permanent: '' },
            supertitle: { value: '', permanent: '' },
            subtitle: { value: '', permanent: '' },
        });
    }
    get urlsStack() {
        return this._stack.urlsStack;
    }
    get title() {
        return this._title$.getValue();
    }
    get title$() {
        return this._title$.asObservable();
    }
    get routeChange() {
        return this._stack.stackRouteChangeSubscription;
    }
    /**
     * @param value
     * @param permanent
     */
    setSupertitle(value, permanent = true) {
        this.setComponent('supertitle', value, permanent);
        this._title$.next(Object.assign(Object.assign({}, this.title), { supertitle: {
                value,
                permanent: permanent ? value : this.title.supertitle.permanent,
            } }));
    }
    /**
     * @param value
     * @param permanent
     */
    setSubtitle(value, permanent = true) {
        this.setComponent('subtitle', value, permanent);
        this._title$.next(Object.assign(Object.assign({}, this.title), { subtitle: {
                value,
                permanent: permanent ? value : this.title.subtitle.permanent,
            } }));
    }
    /**
     * Set all titles
     * @param title
     * @param supertitle
     * @param subtitle
     * @param permanent
     */
    setTitle(title, supertitle, subtitle, permanent = true) {
        this.setComponent('title', title, permanent);
        this.setComponent('supertitle', supertitle, permanent);
        this.setComponent('subtitle', subtitle, permanent);
        const data = Object.assign(Object.assign({}, this.title), { title: {
                value: title,
                permanent: permanent ? title : this.title.title.value,
            } });
        if (supertitle !== undefined) {
            data.supertitle = {
                value: supertitle,
                permanent: permanent ? supertitle : this.title.supertitle.value,
            };
        }
        if (subtitle !== undefined) {
            data.subtitle = {
                value: subtitle,
                permanent: permanent ? supertitle : this.title.subtitle.value,
            };
        }
        this._title$.next(data);
    }
    /**
     * Set action (button) for special target
     * @param name
     * @param action { UrlInfoAction }
     */
    setAction(name, action) {
        this._stack.actions.setAction(name, action);
    }
    /**
     * Set actions (buttons) for special target
     * @param name
     * @param actions
     */
    setActions(name, actions) {
        this._stack.actions.setActions(name, actions);
    }
    /**
     * Show action (button)
     * @param name
     */
    showAction(name) {
        this._stack.actions.showAction(name);
    }
    /**
     * Hide action (button)
     * @param name
     */
    hideAction(name) {
        this._stack.actions.hideAction(name);
    }
    /**
     * Set menu action (item) for special target
     * @param name
     * @param action
     * @param group
     * @param icon
     */
    setMenuAction(name, action, group = 'default', icon = '') {
        this._stack.menus.setMenuAction(name, action, group, icon);
    }
    /**
     * Set menu actions (items) for special target
     * @param name
     * @param actions
     * @param group
     * @param icon
     */
    setMenuActions(name, actions, group = 'default', icon = '') {
        this._stack.menus.setMenuActions(name, actions, group, icon);
    }
    /**
     * @param name
     */
    showMenu(name) {
        this._stack.menus.showMenu(name);
    }
    /**
     * @param name
     */
    hideMenu(name) {
        this._stack.menus.hideMenu(name);
    }
    /**
     * Set menu icon for special target
     * @param name
     * @param icon
     */
    setMenuIcon(name, icon) {
        this._stack.menus.setMenuIcon(name, icon);
    }
    /**
     * Set value for special target
     * @param name
     * @param value
     * @param permanent { boolean } - do not clear component value when url changed
     */
    setComponent(name, value, permanent = null) {
        this._stack.components.setComponentValue(name, value, permanent);
    }
    /**
     * @param name
     */
    showComponent(name) {
        this._stack.components.showComponent(name);
    }
    /**
     *
     * @param name
     */
    hideComponent(name) {
        this._stack.components.hideComponent(name);
    }
    /**
     * @param steps
     */
    goBack(steps = null) {
        this._stack.goBack(steps);
    }
}
FsNavService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavService, deps: [{ token: FsNavStackService }], target: i0.ɵɵFactoryTarget.Injectable });
FsNavService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: FsNavStackService }]; } });

class FsNavTitleService {
    constructor(_browserTitle, _navService, _router) {
        this._browserTitle = _browserTitle;
        this._navService = _navService;
        this._router = _router;
    }
    init() {
        this._navService.title$
            .subscribe((data) => {
            this._setTitle(data.title.value, data.supertitle.value);
        });
        this._router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
            this._setTitle(this._navService.title.title.permanent, this._navService.title.supertitle.permanent);
        });
    }
    getTitle() {
        return this._title;
    }
    _setTitle(title, supertitle) {
        const tile = [];
        if (title) {
            tile.push(title);
        }
        if (supertitle) {
            tile.push(supertitle);
        }
        this._title = tile.join(' · ');
        this._browserTitle.setTitle(this._title);
    }
}
FsNavTitleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavTitleService, deps: [{ token: i1$2.Title }, { token: FsNavService }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
FsNavTitleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavTitleService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavTitleService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$2.Title }, { type: FsNavService }, { type: i1.Router }]; } });

class FsNavModule {
    constructor(_titleService) {
        this._titleService = _titleService;
        this._titleService.init();
    }
    static forRoot(config = {}) {
        return {
            ngModule: FsNavModule,
            providers: [
                { provide: FS_NAV_CONFIG, useValue: config },
                {
                    provide: FS_NAV_DEFAULT_CONFIG,
                    useFactory: FsNavConfigFactory,
                    deps: [FS_NAV_CONFIG]
                },
                FsNavService,
                FsNavStackService,
                FsNavUpdatesService,
                FsNavTitleService,
            ]
        };
    }
}
FsNavModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavModule, deps: [{ token: FsNavTitleService }], target: i0.ɵɵFactoryTarget.NgModule });
FsNavModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavModule, declarations: [FsNavBaseComponent,
        FsNavTitleComponent,
        FsNavSupertitleComponent,
        FsNavSubtitleComponent,
        FsNavActionsComponent,
        FsNavComponentComponent,
        FsNavMenuComponent,
        FsNavBackComponent,
        FsNavBaseComponent], imports: [CommonModule,
        RouterModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule], exports: [FsNavTitleComponent,
        FsNavSubtitleComponent,
        FsNavSupertitleComponent,
        FsNavActionsComponent,
        FsNavComponentComponent,
        FsNavBackComponent,
        FsNavMenuComponent] });
FsNavModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavModule, imports: [[
            CommonModule,
            RouterModule,
            MatButtonModule,
            MatIconModule,
            MatMenuModule,
            MatDividerModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsNavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        MatButtonModule,
                        MatIconModule,
                        MatMenuModule,
                        MatDividerModule,
                    ],
                    exports: [
                        FsNavTitleComponent,
                        FsNavSubtitleComponent,
                        FsNavSupertitleComponent,
                        FsNavActionsComponent,
                        FsNavComponentComponent,
                        FsNavBackComponent,
                        FsNavMenuComponent,
                    ],
                    entryComponents: [],
                    declarations: [
                        FsNavBaseComponent,
                        FsNavTitleComponent,
                        FsNavSupertitleComponent,
                        FsNavSubtitleComponent,
                        FsNavActionsComponent,
                        FsNavComponentComponent,
                        FsNavMenuComponent,
                        FsNavBackComponent,
                        FsNavBaseComponent,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: FsNavTitleService }]; } });
function FsNavConfigFactory(config) {
    return merge({
        watchBrowserBackButton: true
    }, config);
}

/*
 * Public API Surface of fs-menu
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FsNavActions, FsNavActionsComponent, FsNavBackComponent, FsNavBaseComponent, FsNavComponentComponent, FsNavComponents, FsNavMenuComponent, FsNavMenus, FsNavModule, FsNavService, FsNavStackService, FsNavSubtitleComponent, FsNavSupertitleComponent, FsNavTitleComponent, FsNavTitleService, FsNavUpdateTarget, FsNavUpdateType, FsNavUpdatesService, NavAction, NavActionType, NavComponent, NavDropDownMenu };
